import get from 'lodash.get';

import {
    VirtualizedTableColumns,
    VirtualizedTableItem
} from 'services/virtualizedTable/types';
import { TimesheetHoliday } from 'services/timesheetHolidays/types';
import { selectTimesheetHolidaysArray } from 'services/timesheetHolidays/selectors';
import {
    FIELD_TYPE_STRING,
    FIELD_TYPE_DATE,
    FIELD_TYPE_DROPDOWN_MULTIPLE
} from 'components/VirtualizedTable/constants';
import { makeSelectTableColumnWidths } from 'services/columnWidths/selectors';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import { makeSelectEntityTypeFirstDepthValue } from 'services/entityTypes/selectors';
import { selectEntityFirstDepthOptions } from 'services/entities/selectors';
import {
    selectIsAdmin,
    selectIsManager,
    selectIsPayroll
} from 'services/usersMe/selectors';
// @ts-ignore
import { selectCurrentSeasonIsComplete } from 'services/currentSeasonId/selectors';

const selectTimesheetHolidaysTableColumns = createDeepEqualSelector(
    [
        makeSelectTableColumnWidths(),
        makeSelectEntityTypeFirstDepthValue('pluralName'),
        selectEntityFirstDepthOptions
    ],
    (
        tableColumnWidths,
        firstDepthEntityTypeName: string,
        firstDepthEntityOptions
    ): VirtualizedTableColumns => {
        const result: VirtualizedTableColumns = [
            {
                label: 'Name',
                value: 'name',
                width: 250,
                type: FIELD_TYPE_STRING,
                validators: 'isRequired',
                disableUpdating: true
            },
            {
                label: 'Date',
                value: 'date',
                width: 150,
                type: FIELD_TYPE_DATE,
                validators: 'isRequired',
                disableUpdating: true
            },
            {
                label: firstDepthEntityTypeName,
                value: 'entityIds',
                width: 250,
                selectable: false,
                options: firstDepthEntityOptions,
                type: FIELD_TYPE_DROPDOWN_MULTIPLE,
                validators: 'isRequiredArray'
            }
        ];

        return result.map((column) => {
            const { width, value } = column;
            return {
                ...column,
                width: get(tableColumnWidths, value, width)
            };
        });
    }
);

const selectTimesheetHolidayItems = createDeepEqualSelector(
    [selectTimesheetHolidaysArray],
    (timesheetHolidays) =>
        timesheetHolidays.map(
            (timesheetHoliday: TimesheetHoliday): VirtualizedTableItem => {
                const { uuid, id, name, date, entityIds } = timesheetHoliday;
                return {
                    key: uuid,
                    id,
                    name,
                    date,
                    entityIds
                };
            }
        )
);

const makeSelectTimesheetHolidayItems = (): typeof selectTimesheetHolidayItems =>
    selectTimesheetHolidayItems;

const selectIsWritable = createDeepEqualSelector(
    [
        selectIsAdmin,
        selectIsManager,
        selectIsPayroll,
        selectCurrentSeasonIsComplete
    ],
    (
        isAdmin: boolean,
        isManager: boolean,
        isPayroll: boolean,
        currentSeasonIsComplete: boolean
    ): boolean =>
        (isAdmin || isManager || isPayroll) && !currentSeasonIsComplete
);

export {
    selectTimesheetHolidaysTableColumns,
    makeSelectTimesheetHolidayItems,
    selectIsWritable
};
